// Generat automat din README.md - Enhanced cu funcționalități interactive avansate pentru Margarit Alexandru Florian
import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Award, Download, Edit3, Save, X, Clock, TrendingUp, Target, Star, FileText, Printer, Share2, Eye, Calendar as CalendarIcon } from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData } from '../../utils/mockData';

// Toast Component pentru notificări
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: '✓',
    error: '✗',
    warning: '⚠',
    info: 'i'
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`${colors[type]} text-white p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-in`}>
      <span className="text-xl">{icons[type]}</span>
      <span>{message}</span>
      <button onClick={onClose} className="ml-auto">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const newToast = { id, message, type };
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

const PortalAngajati = () => {
  const [employee, setEmployee] = useState(mockData.employees[0]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toasts, showToast, removeToast } = useToast();

  const handleEditStart = () => {
    setEditForm({
      email: employee.email,
      phone: employee.phone,
      skills: employee.skills
    });
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    setLoading(true);
    
    // Simulare API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setEmployee(prev => ({
      ...prev,
      ...editForm
    }));
    
    setIsEditing(false);
    setLoading(false);
    showToast('Profilul a fost actualizat cu succes!', 'success');
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleExportPDF = async (type) => {
    setLoading(true);
    
    // Simulare export PDF
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    showToast(`${type} a fost generat și descărcat cu succes!`, 'success');
    setLoading(false);
    setShowExportModal(false);
  };

  const vacations = mockData.vacations.filter(v => v.employee === employee.name);
  const totalVacationDays = 25;
  const usedDays = vacations.reduce((acc, v) => acc + v.days, 0);
  const remainingDays = totalVacationDays - usedDays;

  // Calcularea statistici de performanță
  const performanceStats = {
    currentGoals: employee.performance?.goals?.length || 0,
    completedGoals: employee.performance?.goals?.filter(g => g.status === 'Finalizat').length || 0,
    avgProgress: employee.performance?.goals?.reduce((acc, g) => acc + g.progress, 0) / (employee.performance?.goals?.length || 1) || 0,
    rating: employee.performance?.rating || 0
  };

  return (
    <div className="space-y-6">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Header cu acțiuni */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Portal Angajați - {employee.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Proprietar & Manager General - Botez de poveste
          </p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={() => setShowExportModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Rapoarte
          </button>
          
          {isEditing ? (
            <div className="flex gap-2">
              <button 
                onClick={handleEditSave}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Salvează
              </button>
              <button 
                onClick={handleEditCancel}
                className="btn-secondary flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Anulează
              </button>
            </div>
          ) : (
            <button 
              onClick={handleEditStart}
              className="btn-primary flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Editează Profil
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profil Personal */}
        <div className="lg:col-span-2">
          <Card title="Profil Personal" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Nume complet</p>
                    <p className="font-semibold">{employee.name}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Award className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Poziție</p>
                    <p className="font-semibold">{employee.position}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        className="input-field"
                      />
                    ) : (
                      <p className="font-semibold">{employee.email}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        className="input-field"
                      />
                    ) : (
                      <p className="font-semibold">{employee.phone}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Data angajării</p>
                    <p className="font-semibold">{new Date(employee.startDate).toLocaleDateString('ro-RO')}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Star className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Rating Performanță</p>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= performanceStats.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold">{performanceStats.rating}/5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Competențe */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-3">Competențe</p>
              {isEditing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Adaugă competențe separate prin virgule"
                    value={editForm.skills?.join(', ') || ''}
                    onChange={(e) => setEditForm({
                      ...editForm, 
                      skills: e.target.value.split(',').map(s => s.trim())
                    })}
                    className="input-field"
                  />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {employee.skills?.map((skill, index) => (
                    <Badge key={index} variant="info" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Statistici Performanță */}
          <Card title="Sumar Performanță" icon={TrendingUp} className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{performanceStats.currentGoals}</div>
                <div className="text-sm text-gray-500">Obiective Curente</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{performanceStats.completedGoals}</div>
                <div className="text-sm text-gray-500">Finalizate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{Math.round(performanceStats.avgProgress)}%</div>
                <div className="text-sm text-gray-500">Progres Mediu</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{performanceStats.rating}</div>
                <div className="text-sm text-gray-500">Rating General</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Concedii */}
          <Card title="Concedii" icon={Calendar}>
            <div className="space-y-4">
              {vacations.map((vacation, index) => (
                <div key={vacation.id} className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                      {vacation.type}
                    </span>
                    <Badge variant={vacation.status === 'Planificat' ? 'warning' : 'success'}>
                      {vacation.status}
                    </Badge>
                  </div>
                  <p className="text-blue-700 dark:text-blue-300">
                    {new Date(vacation.startDate).toLocaleDateString('ro-RO')} - 
                    {new Date(vacation.endDate).toLocaleDateString('ro-RO')}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {vacation.days} zile
                  </p>
                </div>
              ))}
              
              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span>Zile totale</span>
                  <span className="font-semibold">{totalVacationDays} zile</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Zile utilizate</span>
                  <span className="font-semibold">{usedDays} zile</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Zile rămase</span>
                  <span className="font-semibold text-green-600">{remainingDays} zile</span>
                </div>
              </div>
              
              <button 
                onClick={() => setShowVacationModal(true)}
                className="w-full btn-primary"
              >
                Solicită Concediu
              </button>
            </div>
          </Card>

          {/* Program Lucru */}
          <Card title="Program de Lucru" icon={Clock}>
            <div className="space-y-2 text-sm">
              {Object.entries(employee.workingHours || {}).map(([day, hours]) => (
                <div key={day} className="flex justify-between">
                  <span className="capitalize">
                    {day === 'monday' ? 'Luni' :
                     day === 'tuesday' ? 'Marți' :
                     day === 'wednesday' ? 'Miercuri' :
                     day === 'thursday' ? 'Joi' :
                     day === 'friday' ? 'Vineri' :
                     day === 'saturday' ? 'Sâmbătă' :
                     'Duminică'}
                  </span>
                  <span className={hours === 'Liber' ? 'text-green-600' : 'font-medium'}>
                    {hours}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Acțiuni Rapide */}
          <Card title="Acțiuni Rapide">
            <div className="space-y-3">
              <button 
                onClick={() => handleExportPDF('Adeverință de Salariat')}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Adeverință Salariat</span>
                </div>
                <span className="text-xs text-gray-500">PDF</span>
              </button>
              
              <button 
                onClick={() => handleExportPDF('Fluturaș Salariu')}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Fluturaș Salariu</span>
                </div>
                <span className="text-xs text-gray-500">Martie 2024</span>
              </button>
              
              <button 
                onClick={() => handleExportPDF('Raport Pontaj')}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Printer className="w-4 h-4" />
                  <span className="text-sm font-medium">Raport Pontaj</span>
                </div>
                <span className="text-xs text-gray-500">Luna curentă</span>
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal Export */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Rapoarte"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Selectează tipul de raport pe care vrei să îl exportezi:
          </p>
          
          <div className="space-y-2">
            {[
              'Adeverință de Salariat',
              'Fluturaș Salariu - Martie 2024',
              'Raport Pontaj - Luna curentă',
              'Istoric Concedii 2024',
              'Evaluare Performanță',
              'Certificat de Lucru'
            ].map((reportType) => (
              <button
                key={reportType}
                onClick={() => handleExportPDF(reportType)}
                disabled={loading}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">{reportType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">PDF</span>
                  <Download className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setShowExportModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Solicită Concediu */}
      <Modal
        isOpen={showVacationModal}
        onClose={() => setShowVacationModal(false)}
        title="Solicită Concediu"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tipul concediului</label>
            <select className="input-field">
              <option>Concediu de odihnă</option>
              <option>Concediu medical</option>
              <option>Zi liberă</option>
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Data început</label>
              <input type="date" className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Data sfârșit</label>
              <input type="date" className="input-field" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Motiv (opțional)</label>
            <textarea 
              className="input-field"
              rows="3"
              placeholder="Motivul solicitării..."
            />
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Zile disponibile:</strong> {remainingDays} zile
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowVacationModal(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
            <button className="btn-primary">
              Trimite Solicitarea
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PortalAngajati;